/**
 * Measurements (spec 3.20, Phase 6): the units a way to ask may use, what each measures, and the one reader and
 * writer of a measurement as text. PURE.
 * - A way may change a question's unit only within one dimension (unitDimension: variantAllowed's unit rule).
 * - An answer given in a unit is stored as canonical text: the amount with its unit, in the unit it was given in
 *   ("170 cm", `5'7"`), so nothing is ever rounded on its way into storage.
 * - A stored measurement is shown converted to the unit of the way that displays it (rounded to that unit's
 *   precision), or exactly as given when that is its own unit. Text that is not a measurement is never touched.
 */
const UNITS = [
    { id: 'mm', dimension: 'length', base: 0.001, words: ['mm', 'millimeter', 'millimeters', 'millimetre', 'millimetres'], decimals: 0 },
    { id: 'cm', dimension: 'length', base: 0.01, words: ['cm', 'centimeter', 'centimeters', 'centimetre', 'centimetres'], decimals: 1 },
    { id: 'm', dimension: 'length', base: 1, words: ['m', 'meter', 'meters', 'metre', 'metres'], decimals: 2 },
    { id: 'km', dimension: 'length', base: 1000, words: ['km', 'kilometer', 'kilometers', 'kilometre', 'kilometres'], decimals: 1 },
    { id: 'in', dimension: 'length', base: 0.0254, words: ['in', 'inch', 'inches', '"'], decimals: 1 },
    { id: 'ft', dimension: 'length', base: 0.3048, words: ['ft', 'foot', 'feet', "'"], decimals: 1 },
    { id: 'mi', dimension: 'length', base: 1609.344, words: ['mi', 'mile', 'miles'], decimals: 1 },
    { id: 'hands', dimension: 'length', base: null, words: ['hand', 'hands', 'hh'], decimals: 1 },
    { id: 'g', dimension: 'weight', base: 0.001, words: ['g', 'gram', 'grams'], decimals: 0 },
    { id: 'kg', dimension: 'weight', base: 1, words: ['kg', 'kgs', 'kilo', 'kilos', 'kilogram', 'kilograms'], decimals: 1 },
    { id: 'oz', dimension: 'weight', base: 0.028349523125, words: ['oz', 'ounce', 'ounces'], decimals: 1 },
    { id: 'lb', dimension: 'weight', base: 0.45359237, words: ['lb', 'lbs', 'pound', 'pounds'], decimals: 1 },
    { id: 'st', dimension: 'weight', base: 6.35029318, words: ['st', 'stone', 'stones'], decimals: 1 },
];
/** Feet and inches (`5'7"`): the one two-part unit. */
export const FEET_AND_INCHES = 'ft/in';
const FEET_AND_INCHES_UNIT = { id: FEET_AND_INCHES, dimension: 'length', base: 0.0254, words: [FEET_AND_INCHES], decimals: 1 }; // base: per inch
const BY_WORD = new Map([...UNITS, FEET_AND_INCHES_UNIT].flatMap((u) => u.words.map((w) => [w, u])));
const BY_ID = new Map([...UNITS, FEET_AND_INCHES_UNIT].map((u) => [u.id, u]));
/** Words that are also English words or single letters: a unit only among other unit words. */
const AMBIGUOUS = new Set(['in', 'm', 'g', 'st', 'hand']);
const JOINERS = new Set(['or', 'and']);
/** US, UK and EU shoe sizes: a table, not a factor (never converted). */
const SHOE_SIZE = /shoe|\b(eu|uk)\b|\bus (men|women|kids)/i;
/** The words of a unit text, `ft/in` kept whole. */
function unitWords(unit) {
    return (unit.toLowerCase().match(/[a-z]+(?:\/[a-z]+)*/g) ?? []).flatMap((w) => (w === FEET_AND_INCHES ? [w] : w.split('/')));
}
/**
 * The units a unit text names: each unambiguous unit word, and an ambiguous one ("in", "m", "g", "st") only among
 * other units ("in or cm", "ft or m", or a text of nothing but units), so "per hour, in your rate currency" or
 * "e.g. 25-35" name none. "feet and inches" names feet and inches.
 */
function namedUnits(unit) {
    const words = unitWords(unit);
    const named = words.map((w) => BY_WORD.get(w));
    const onlyUnits = words.every((w, i) => named[i] || JOINERS.has(w));
    const clear = new Set(named.filter((u, i) => u && !AMBIGUOUS.has(words[i])).map((u) => u.dimension));
    return named.filter((u, i) => !!u && (!AMBIGUOUS.has(words[i]) || onlyUnits || clear.has(u.dimension)));
}
/** What a unit text measures: length, weight or shoe size; any other unit measures only itself. */
export function unitDimension(unit) {
    if (!unit?.trim())
        return null;
    if (SHOE_SIZE.test(unit))
        return 'shoe_size';
    const dimensions = new Set(namedUnits(unit).map((u) => u.dimension));
    return dimensions.size === 1 ? [...dimensions][0] : unit.trim().toLowerCase();
}
/** The ONE unit a unit text names ("cm", "inches (barefoot)", "ft/in", "feet and inches"), else null ("in or cm"). */
export function singleUnit(unit) {
    if (!unit)
        return null;
    const ids = [...new Set(namedUnits(unit).map((u) => u.id))].sort();
    if (ids.length === 2 && ids[0] === 'ft' && ids[1] === 'in')
        return FEET_AND_INCHES;
    return ids.length === 1 ? ids[0] : null;
}
/** Lowercase, typographic quotes as ' and ", a decimal comma as a point, thousands commas dropped. */
function normalized(text) {
    return text
        .trim()
        .toLowerCase()
        .replace(/[\u2018\u2019\u2032]/g, "'")
        .replace(/[\u201c\u201d\u2033]|''/g, '"')
        .replace(/(\d),(\d{3})(?!\d)/g, '$1$2')
        .replace(/(\d),(\d{1,2})(?!\d)/g, '$1.$2');
}
/** One number and the unit word after it, after an optional separator. */
const PART = /\s*(?:(?:,|and)\s*)?(\d+(?:\.\d+)?)\s*([a-z]+|['"])?\.?/y;
/**
 * A measurement typed as text: a number and its unit ("170 cm", "62.5kg", "49 in"), or feet and inches (`5'7"`,
 * `5' 7`, "5 ft 7 in"); nothing else in the text. A bare number is in `unit` when that names one unit (never for
 * feet and inches, where "5" says too little), and a unit given must measure what `unit` does. Null otherwise.
 */
export function parseMeasure(text, unit) {
    const s = normalized(text);
    const parts = [];
    let at = 0;
    while (at < s.length) {
        PART.lastIndex = at;
        const m = PART.exec(s);
        if (!m || PART.lastIndex === at)
            break;
        parts.push({ n: m[1], w: m[2] });
        at = PART.lastIndex;
    }
    if (!parts.length || parts.length > 2 || s.slice(at).trim())
        return null;
    const expected = unit ? singleUnit(unit) : null;
    const dimension = unit ? unitDimension(unit) : null;
    let found = null;
    if (parts.length === 1) {
        const [{ n, w }] = parts;
        const u = w ? BY_WORD.get(w) : expected && expected !== FEET_AND_INCHES ? BY_ID.get(expected) : undefined;
        if (u?.base)
            found = { unit: u.id, dimension: u.dimension, amount: Number(n) * u.base, numbers: [n] };
    }
    else {
        const [feet, inches] = parts;
        if (BY_WORD.get(feet.w ?? '')?.id === 'ft' && (!inches.w || BY_WORD.get(inches.w)?.id === 'in'))
            found = { unit: FEET_AND_INCHES, dimension: 'length', amount: (Number(feet.n) * 12 + Number(inches.n)) * 0.0254, numbers: [feet.n, inches.n] };
    }
    if (!found || !Number.isFinite(found.amount))
        return null;
    return dimension && dimension !== found.dimension ? null : found;
}
/** The canonical text of a measurement, in the unit it was given in: "170 cm", `5'7"`. */
export function measureText(m) {
    return m.unit === FEET_AND_INCHES ? `${m.numbers[0]}'${m.numbers[1]}"` : `${m.numbers[0]} ${m.unit}`;
}
/** A number with at most `decimals` decimals, trailing zeros dropped. */
const fixed = (x, decimals) => String(Number(x.toFixed(decimals)) || 0);
/**
 * A measurement as a way asking in `unit` shows it: the number alone in that unit ("170"), feet and inches as `5'7"`;
 * the numbers exactly as given when it was given in that unit. Null when `unit` names no single unit, or measures
 * something else.
 */
export function measureIn(m, unit) {
    const id = singleUnit(unit);
    const to = id ? BY_ID.get(id) : undefined;
    if (!to || to.dimension !== m.dimension)
        return null;
    if (to.id === m.unit)
        return to.id === FEET_AND_INCHES ? `${m.numbers[0]}'${m.numbers[1]}"` : m.numbers[0];
    if (!to.base)
        return null;
    if (to.id !== FEET_AND_INCHES)
        return fixed(m.amount / to.base, to.decimals);
    const inches = Math.round((m.amount / 0.0254) * 2) / 2; // feet and inches: to the half inch
    const feet = Math.floor(inches / 12);
    return `${feet}'${fixed(inches - feet * 12, 1)}"`;
}
//# sourceMappingURL=measure.js.map